import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  DataTable,
  DataToolBar,
} from "@b2b/ui-components";
import { rfqService } from "@b2b/services";
import { Rfq } from "@b2b/services/api/modules/rfq/rfq.types";
import { ClipboardList, MessageSquare, Clock, Eye, AlertCircle } from "lucide-react";
import { RFQDisputeDialog } from "@b2b/ui-components/features/RFQ/components/RFQDisputeDialog";

interface ToastState {
  type: "success" | "error";
  message: string;
}

interface ToolbarState {
  q: string;
  status: string;
  country: string;
  from?: Date;
  to?: Date;
  pageSize: number;
}

export default function SellerRfqInbox() {
  const [rfqs, setRfqs] = useState<Rfq[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [toolbar, setToolbar] = useState<ToolbarState>({
    q: "",
    status: "all",
    country: "all",
    pageSize: 10,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [selectedRfqForDispute, setSelectedRfqForDispute] = useState<string | undefined>(undefined);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const setToolbarPartial = (updates: Partial<ToolbarState>) => {
    setToolbar((prev) => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    fetchRfqs();
  }, [currentPage, toolbar]);

  const fetchRfqs = async () => {
    try {
      setLoading(true);
      // Map toolbar status to API status
      let apiStatus: string | undefined = undefined;
      if (toolbar.status === "open") apiStatus = "OPEN";
      else if (toolbar.status === "closed") apiStatus = "CLOSED";
      else if (toolbar.status === "cancelled") apiStatus = "CANCELLED";
      
      const response = await rfqService.getRfqList({
        page: currentPage,
        limit: toolbar.pageSize,
        search: toolbar.q || undefined,
        status: apiStatus,
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      if (response && response.items) {
        setRfqs(response.items);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error: any) {
      console.error("Error fetching RFQs:", error);
      showToast("error", error?.response?.data?.message || "Failed to load RFQs");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
  };

  const getDaysUntilExpiry = (expiryDate?: string) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  return (
    <div className="min-h-screen bg-background p-6">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 p-4 rounded-lg shadow-lg max-w-[400px] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <p className="flex-1 font-medium text-sm">{toast.message}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <ClipboardList className="w-8 h-8" />
              RFQ Inbox
            </h1>
            <p className="text-muted-foreground mt-1">
              Browse and respond to RFQs matching your product categories
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <DataToolBar
              state={toolbar}
              setState={setToolbarPartial}
              countries={[]}
              onExport={() => {
                // Export functionality can be added later
                showToast("info", "Export functionality coming soon");
              }}
              onReset={() => {
                setToolbar({
                  q: "",
                  status: "all",
                  country: "all",
                  pageSize: 10,
                });
              }}
              onCreate={undefined}
            />
          </CardContent>
        </Card>

        {/* RFQ List */}
        <Card>
          <CardHeader>
            <CardTitle>Available RFQs ({rfqs.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading RFQs...</div>
            ) : rfqs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No RFQs available at the moment
              </div>
            ) : (
              <div className="space-y-4">
                {rfqs.map((rfq) => {
                  const daysUntilExpiry = getDaysUntilExpiry(rfq.expiryDate);
                  return (
                    <div
                      key={rfq.id}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-foreground">{rfq.title}</h3>
                            <Badge className={getStatusColor(rfq.status)}>{rfq.status}</Badge>
                            {daysUntilExpiry !== null && daysUntilExpiry <= 7 && (
                              <Badge variant="destructive" className="text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                {daysUntilExpiry} days left
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                            {rfq.description}
                          </p>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <div>
                              <span className="font-medium">Quantity:</span> {rfq.quantity}{" "}
                              {rfq.unit}
                            </div>
                            {rfq.categories && rfq.categories.length > 0 && (
                              <div>
                                <span className="font-medium">Categories:</span>{" "}
                                {rfq.categories.map((c) => c.name).join(", ")}
                              </div>
                            )}
                            <div>
                              <span className="font-medium">Posted:</span> {formatDate(rfq.createdAt)}
                            </div>
                            {rfq.expiryDate && (
                              <div>
                                <span className="font-medium">Expires:</span>{" "}
                                {formatDate(rfq.expiryDate)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <Button asChild size="sm" variant="primary">
                            <Link to={`/seller/rfq/${rfq.id}/quote`}>
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Submit Quote
                            </Link>
                          </Button>
                          <Button asChild size="sm" variant="outline">
                            <Link to={`/rfq/${rfq.id}`}>
                              <Eye className="w-4 h-4 mr-1" />
                              View Details
                            </Link>
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedRfqForDispute(rfq.id);
                              setDisputeDialogOpen(true);
                            }}
                          >
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Raise Dispute
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dispute Dialog */}
      {selectedRfqForDispute && (
        <RFQDisputeDialog
          open={disputeDialogOpen}
          onOpenChange={setDisputeDialogOpen}
          rfqId={selectedRfqForDispute}
          mode="create"
          onDisputeCreated={() => {
            fetchRfqs();
            setSelectedRfqForDispute(undefined);
          }}
        />
      )}
    </div>
  );
}

